import { useState } from "react";
import config from "config/config";
import { v4 as uuidv4 } from "uuid";
import Countdown from "react-countdown";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FormInputError from "components/General/FormInputError";
import { registerAgent } from "service/userservice/registerAgent";
import DomainErrorModal from "components/ModalDialogs/DomainErrorModal";
import { sendEmailNotification } from "service/notificationservice/sendEmailNotification";
import RegistrationConfirmModal from "components/ModalDialogs/RegistrationConfirmModal";

const StepFour = ({
  agentRegistrationFormData,
  setAgentRegistrationFormData,
  setCurrentStep,
  setGeneratedPassCode,
  generatedPassCode,
  roleid,
  setLoading,
}) => {
  const { t } = useTranslation();
  const [modalState, setModalState] = useState(false);
  const [errorInfo, seterrorInfo] = useState("");
  const [modalErrorState, setModalErrorState] = useState(false);
  const [userEmail, setUserEmail] = useState(agentRegistrationFormData.email);
  const [userName, setUserName] = useState("");
  const [currentCounterTime, setCurrentCounterTime] = useState(
    Date.now() + parseInt(config.OTP_EXPIRY_TIME)
  );
  const [passcodeExpired, setPasscodeExpired] = useState(false);
  const [currentCounterIndex, setCurrentCounterIndex] = useState(0);
  const uuid = uuidv4();

  const {
    register: registrationStepFourRegister,
    handleSubmit,
    formState: { errors: registrationStepFourErrors },
  } = useForm();

  function randomNumberInRange(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  const renderer = ({ minutes, seconds, completed }) => {
    if (completed) {
      setPasscodeExpired(true);
      return (
        <p className="text-sm">{t("AgentRegister-step4-resendCodeText")}</p>
      );
    } else {
      return (
        <p className="text-sm">
          {t("AgentRegister-step4-codeExpiresText")}
          {minutes > 9 ? minutes : "0" + minutes}:
          {seconds > 9 ? seconds : "0" + seconds}
        </p>
      );
    }
  };

  async function resendCode() {
    let passcode = randomNumberInRange(100000, 999999);
    setGeneratedPassCode(passcode.toString());
    let securitycode = randomNumberInRange(100000, 999999);
    let sec =
      parseInt(securitycode.toString().substring(3, 4)) === 0
        ? 1
        : parseInt(securitycode.toString().substring(3, 4));
    let confirmationcode = passcode * sec;
    setLoading(true);
    let contentDictionary = {};
    contentDictionary.confirmationcode = confirmationcode.toString();
    contentDictionary.username = userName;
    contentDictionary.securitycode = securitycode.toString();

    await sendEmailNotification(userEmail, 12, contentDictionary).catch(
      (error) => {
        console.log(error);
      }
    );
    setLoading(false);
    setCurrentCounterTime(Date.now() + parseInt(config.OTP_EXPIRY_TIME));
    setCurrentCounterIndex(currentCounterIndex + 1);
    setPasscodeExpired(false);
  }

  async function onSubmit(formData) {
    setAgentRegistrationFormData((curr) => ({ ...curr, ...formData }));
    let data = agentRegistrationFormData;
    setUserEmail(data.email);
    setUserName(data.firstname);
    if (passcodeExpired) {
      seterrorInfo(t("AgentRegister-step4-onSubmitCodeExpiredInfo"));
      setModalErrorState(true);
    } else {
      if (
        formData.registrationConfirmationCode === generatedPassCode ||
        (config.ENABLE_DEFAULT_OTP === "true" &&
          formData.registrationConfirmationCode === config.DEFAULT_OTP)
      ) {
        let UserSecurityQADto = [];
        const SA1 = {
          QuestionId: parseInt(data.securityQuestionOne),
          UserAnswer: data.securtityAnswerOne.replaceAll(/\s/g, ""),
        };
        UserSecurityQADto.push(SA1);

        const SA2 = {
          QuestionId: parseInt(data.securityQuestionTwo),
          UserAnswer: data.securtityAnswerTwo.replaceAll(/\s/g, ""),
        };
        UserSecurityQADto.push(SA2);

        const SA3 = {
          QuestionId: parseInt(data.securityQuestionThree),
          UserAnswer: data.securtityAnswerThree.replaceAll(/\s/g, ""),
        };
        UserSecurityQADto.push(SA3);

        const notificationPreference = data.emailNotification ? 1 : 2;
        setLoading(true);
        await registerAgent(
          data.alternateId,
          data.agentIdNo,
          data.firstname,
          data.lastname,
          data.identityNumber,
          data.zipCode,
          data.phoneNumber,
          notificationPreference,
          data.email,
          data.username,
          data.hashedPassword.toString(),
          roleid,
          UserSecurityQADto
        );
        setLoading(false);
        setModalState(true);
      } else {
        seterrorInfo(t("AgentRegister-step4-onSubmitCodeVerificationInfo"));
        setModalErrorState(true);
      }
    }
  }

  return (
    <>
      <form
        className="pt-4 mt-2 lg:pt-6 border-t border-theme-rule-medium"
        onSubmit={handleSubmit(onSubmit)}
      >
        <p className="text-sm">{t("AgentRegister-step4-helpText-1")}</p>
        <p className="text-sm">{t("AgentRegister-step4-helpText-2")}</p>
        <div className="mt-4">
          <label id="ID_confirmationCodeLabel">
            {t("AgentRegister-step4-confirmationCode-label")}
          </label>
          <input
            id="ID_confirmationCode"
            className="w-full"
            type="text"
            aria-labelledby="ID_confirmationCodeLabel"
            {...registrationStepFourRegister("registrationConfirmationCode", {
              required: t("AgentRegister-step4-confirmationCode-requiredText"),
              pattern: {
                value: /^\d{6}$/,
                message: t("AgentRegister-step4-confirmationCodeError"),
              },
            })}
          />
          {registrationStepFourErrors.registrationConfirmationCode && (
            <FormInputError
              id={`${uuid}-registrationConfirmationCodeError`}
              errorMessage={
                registrationStepFourErrors.registrationConfirmationCode.message
              }
            />
          )}
        </div>
        <div className="mt-4 md:mt-6">
          <Countdown
            date={currentCounterTime}
            key={currentCounterIndex}
            renderer={renderer}
          />
        </div>
        <div className="mt-4 text-right">
          <button
            type="button"
            className="ui-btn-secondary ui-btn-min-w mt-2"
            onClick={() => setCurrentStep(3)}
          >
            {t("AgentRegister-step4-previousButton")}
            <span className="sr-only">
              {t("AgentRegister-step4-previousButton-srText")}
            </span>
          </button>
          <button
            className="ui-btn-secondary ui-btn-min-w mt-2"
            type="button"
            onClick={() => resendCode()}
          >
            {t("AgentRegister-step4-resendCodeButton")}
          </button>
          <button type="submit" className="ui-btn-primary ui-btn-min-w mt-2">
            {t("AgentRegister-step4-submitButton")}
          </button>
        </div>
      </form>

      <RegistrationConfirmModal
        modalState={modalState}
        setModalState={setModalState}
      />
      <DomainErrorModal
        modalState={modalErrorState}
        setModalState={setModalErrorState}
        errorInfo={errorInfo}
      />
    </>
  );
};

export default StepFour;
